import "animate.css";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Mainlogo from "../../Image/Main_logo.png";
import { loginUser } from "../../Redux/Slice/Auth";
import "./Login.css";

const Login = ({ onLoginSuccess }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    try {
      await dispatch(loginUser({ Email: email, Password: password })).unwrap();
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      window.location.href = "/";
    } catch (error) {
      const errorMessage =
        error?.message || (typeof error === "string" ? error : "Login failed");

      toast.dismiss();
      toast.error(`Login failed: ${errorMessage}`, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white font-sans overflow-hidden">
      {/* Left Section */}
      <div className="relative flex-1 bg-[#1a2c4e] text-white p-6 md:p-12 lg:p-16 flex flex-col justify-between items-center text-center lg:text-left lg:items-start overflow-hidden">
        {/* Welcome Text */}
        <div className="w-full max-w-lg mt-8 lg:mt-0 animate__animated animate__fadeInDown">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            TASK MANAGEMENT SYSTEM
          </h1>
          <p className="text-xl md:text-2xl font-light mb-4">
            Welcome to dynamicsoftlink
          </p>
          <p className="text-base md:text-lg opacity-90 leading-relaxed animate__animated animate__fadeInUp animate__delay-1s">
            Effortlessly fill surveys with power with the power of voice.
            Simplify data collection, improve response rates, and create an
            engaging experience for your users!
          </p>
        </div>

        {/* Chat Bubbles - Responsive Design */}
        <div className="chat-bubbles-container hidden md:block mt-6">
          <div className="chat-bubbles-container ms-5 md:flex flex-col gap-3 absolute bottom-0 left-0 right-0 px-6 pb-12 w-full animate__animated animate__fadeIn animate__delay-2s">
            {/* Bubble 1 */}
            <div className="chat-bubble-1 text-white text-sm self-start animate__animated animate__bounceIn animate__delay-2s">
              <div className="bg-white/20 bg-opacity-50 backdrop-filter backdrop-blur-sm rounded-lg p-4 mb-4 max-w-sm w-full text-left self-start relative">
                <div className="absolute -top-3 left-4 bg-white/20 bg-opacity-50 backdrop-filter backdrop-blur-sm rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold">
                  DS {/* Placeholder initials/text */}
                </div>
                <p className="text-sm pt-2">
                  Hello, this is dynamicsoftlink, your task management system.
                </p>
              </div>
            </div>

            {/* Bubble 2 */}
            <div className=" text-white px-4 py-2 text-sm self-end animate__animated animate__bounceIn animate__delay-2-5s">
              <div className="bg-blue-600 rounded-lg p-4 max-w-sm w-full text-right self-end relative">
                <div className="absolute -top-3 right-4 bg-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold text-white">
                  U {/* Placeholder initials/text */}
                </div>
                <p className="text-sm pt-2">Yes, I'm ready. Let's go</p>
              </div>
            </div>

            {/* Bubble 3 */}
            {/* <div className="bg-white/20  text-white px-4 py-2 rounded-full text-sm self-start animate__animated animate__bounceIn animate__delay-3s">
            Voice is amazing!
          </div> */}
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-12 lg:p-16 bg-white">
        <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8 md:p-10 animate__animated animate__fadeInUp">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={Mainlogo}
              alt="Dynamic Softlink"
              className="w-44 animate__animated animate__zoomIn"
            />
          </div>

          <h2 className="text-2xl font-semibold text-gray-800 mb-2 animate__animated animate__fadeIn animate__delay-0-5s">
            Log in
          </h2>
          <p className="text-gray-600 mb-6 animate__animated animate__fadeIn animate__delay-0-7s">
            Welcome back! Please enter your details
          </p>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-gray-700 text-sm font-medium mb-2"
              >
                Email*
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="dynamicsoftlink@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-gray-700 text-sm font-medium mb-2"
              >
                Password*
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="*****"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6 text-sm">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe" className="ml-2 text-gray-700">
                  Remember me
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
            {error && (
              <p className="text-red-500 text-center text-sm mt-2 animate__animated animate__shakeX">
                {error}
              </p>
            )}
          </form>

          {/* Uncomment if you want signup link */}
          {/* <p className="text-center text-gray-600 text-sm mt-6">
            Don’t have an account?{" "}
            <Link to="/register" className="text-blue-600 hover:underline">
              Sign up
            </Link>
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default Login;
